/**
 * Store for managing error focus state between editor and error cards
 * Enables bi-directional auto-scrolling and focus animations
 */

import { writable, derived } from 'svelte/store'

export interface ErrorFocusState {
  focusedErrorId: string | null
  source: 'editor' | 'card' | null // Track where the focus originated
  timestamp: number // To prevent circular focus updates
}

function createErrorFocusStore() {
  const { subscribe, set, update } = writable<ErrorFocusState>({
    focusedErrorId: null,
    source: null,
    timestamp: 0
  })
  
  return {
    subscribe,

    /**
     * Set focused error from editor hover
     */
    focusFromEditor(errorId: string | null) {
      set({
        focusedErrorId: errorId,
        source: errorId ? 'editor' : null,
        timestamp: Date.now()
      })
    },

    /**
     * Set focused error from card hover
     */
    focusFromCard(errorId: string | null) {
      set({
        focusedErrorId: errorId,
        source: errorId ? 'card' : null,
        timestamp: Date.now()
      })
    },
    
    /**
     * Clear focus
     */
    clearFocus() {
      set({
        focusedErrorId: null,
        source: null,
        timestamp: 0
      })
    },
    
    /**
     * Check if should respond to focus change
     * Prevents circular updates by checking timestamp
     */
    shouldRespond(lastResponseTime: number): boolean {
      const state = writable<ErrorFocusState>({
        focusedErrorId: null,
        source: null,
        timestamp: 0
      })
      let currentState: ErrorFocusState = { focusedErrorId: null, source: null, timestamp: 0 }
      state.subscribe(value => currentState = value)()
      
      // Only respond if focus changed after last response
      return currentState.timestamp > lastResponseTime
    }
  }
}

export const errorFocusStore = createErrorFocusStore()

// Derived store for convenient access to focused error ID
export const focusedErrorId = derived(
  errorFocusStore,
  $errorFocus => $errorFocus.focusedErrorId
)

// Derived store for focus source
export const focusSource = derived(
  errorFocusStore,
  $errorFocus => $errorFocus.source
)